import {
  Component,
  computed,
  inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  WritableSignal,
  ChangeDetectionStrategy,
} from "@angular/core";

import { SvgIconComponent } from "../../components/svg-icon/svg-icon.component";
import { TranslateModule } from "@ngx-translate/core";
import { DecimalPipe, NgClass } from "@angular/common";
import { ServiceContractService } from "../../../services/api/service-contract/service-contract.service";
import { GateComponentsService } from "../../../services/api/gate-components/gate-components.service";
import { ASSET_TYPES } from "../../../enums/assetTypes.enum";

import { DateTime } from "luxon";
import { combineLatest, map, Subscription } from "rxjs";

import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { GateIdentifierPipe } from "../../pipes/gateIdentifier/gate-identifier.pipe";
import { TooltipModule } from "@openng/optimus-ui/tooltip";
import { PermissionService } from "../../../services/api/permission/permission.service";
import { createTimeoutSignal } from "../../utils/timeout.utils";
import { TimeoutComponent } from "../../components/timeout/timeout.component";
import { isNotNullOrUndefined } from "../../utils/utils";

import { PredServiceLoadEntry, PredServiceTimeEntry, ServiceEntry } from "../../../models/ui/serviceListConfig";

import { GateSystemRef } from "../../../models/shared/refs";

import { ServiceContract } from "../../../models/api/service/serviceContract";
import { AssetHierarchyDTO } from "../../../models/api/asset/assetHierarchyDTO";
import { PredictiveService } from "../../../models/api/gateComponents/predictiveService";
import { RealtimeTelemetryMessage } from "../../../models/ws/realtimeTelemetry";
import { AssetHierarchyService } from "../../../services/api/asset-hierarchy/asset-hierarchy.service";
import {
  TimeseriesSubscriptionHandle,
  TimeseriesWebsocketService,
} from "../../../services/websocket/timeseries-websocket/timeseries-websocket.service";
import { Router } from "@angular/router";

@Component({
  standalone: true,
  selector: "app-service-list",
  imports: [
    SvgIconComponent,
    TranslateModule,
    NgClass,
    DecimalPipe,
    ProgressSpinnerModule,
    TooltipModule,
    TimeoutComponent,
  ],
  templateUrl: "./service-list.component.html",
  styleUrl: "./service-list.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [],
})
export class ServiceListComponent implements OnInit, OnChanges, OnDestroy {
  public asset = input<{ id: string | null; assetType: string }>();
  public type = input<"contracts" | "predictive">();

  public serviceListConfig = {
    title: "Predictive",
    icon: "light_bulb",
  };
  private gateComponentsService = inject(GateComponentsService);
  private assetHierarchyService = inject(AssetHierarchyService);
  public permissions = inject(PermissionService);
  public gateIdentifierPipe = inject(GateIdentifierPipe);
  private serviceCotractService = inject(ServiceContractService);
  private timeseriesWebsocket = inject(TimeseriesWebsocketService);
  private timeseriesSubscription?: TimeseriesSubscriptionHandle;
  private activeLoadSubscription?: Subscription;
  private router = inject(Router);

  public loading: WritableSignal<boolean> = signal(true);
  public timeOut = createTimeoutSignal(this.loading);

  private predictiveServices: WritableSignal<PredictiveService[]> = signal([]);
  private serviceContracts: WritableSignal<ServiceContract[]> = signal([]);

  public selectedTab = signal("");
  public tabs = computed(() => {
    const type = this.type();
    switch (type) {
      case "contracts":
        return [
          { label: "service_contracts.tabs.upcoming", value: "upcoming" },

          { label: "service_contracts.tabs.completed", value: "completed" },
        ];
      case "predictive":
        return [
          { label: "predictive_services.tabs.time", value: "remainingTime" },
          {
            label: "predictive_services.tabs.load_changes",
            value: "loadChanges",
          },
        ];
      default:
        return [];
    }
  });

  public columns = computed(() => {
    const type = this.type();
    const selectedTab = this.selectedTab();
    switch (type) {
      case "contracts":
        switch (selectedTab) {
          case "upcoming":
            return [
              "service_contracts.columns.order_number",
              "service_contracts.columns.label",
              "service_contracts.columns.date",
            ];
          case "completed":
            return [
              "service_contracts.columns.order_number",
              "service_contracts.columns.label",
              "service_contracts.columns.date",
            ];
          default:
            return [];
        }
      case "predictive":
        switch (selectedTab) {
          case "remainingTime":
            return [
              "predictive_services.columns.gate_system",
              "predictive_services.columns.gate_component",
              "predictive_services.columns.due_date",
            ];
          case "loadChanges":
            return [
              "predictive_services.columns.gate_system",
              "predictive_services.columns.gate_component",
              "predictive_services.columns.load_changes_remain",
            ];
          default:
            return [];
        }

      default:
        return [];
    }
  });

  public displayedEntries = computed(() => {
    const type = this.type();
    const predictiveServices = this.predictiveServices();
    const serviceContracts = this.serviceContracts();
    const selectedTab = this.selectedTab();

    let entries: PredServiceLoadEntry | ServiceEntry = [];

    switch (type) {
      case "predictive":
        switch (selectedTab) {
          case "remainingTime":
            return this.convertToComponentsByTime(predictiveServices);
          case "loadChanges":
            entries = this.convertToComponentsByLoadChanges(predictiveServices);
            break;
          default:
            entries = [];
        }
        break;
      case "contracts": {
        const { pastContracts, futureContracts } = this.extractPastAndFutureContracts(serviceContracts);
        switch (selectedTab) {
          case "completed":
            entries = pastContracts;
            break;
          case "upcoming":
            entries = futureContracts;
            break;
          default:
            entries = [];
        }
        break;
      }
      default:
        entries = [];
    }
    return [...entries];
  });

  public ngOnInit() {
    this.initWidget();
  }
  public ngOnChanges(changes: SimpleChanges): void {
    const incomingData = changes["asset"];
    if (!incomingData?.firstChange && this.serviceListConfig) {
      this.initWidget();
    }
  }

  public ngOnDestroy() {
    this.activeLoadSubscription?.unsubscribe();
    this.activeLoadSubscription = undefined;
    this.timeseriesSubscription?.close();
  }

  public navigateToDetail() {
    const type = this.type();
    switch (type) {
      case "predictive":
        this.router.navigate(["dashboard/serviceOrders"], { queryParams: { tab: "0" } });
        break;
      case "contracts":
        this.router.navigate(["dashboard/serviceOrders"], { queryParams: { tab: "1" } });
        break;
      default:
        break;
    }
  }

  /**
   * Initializes the widget based on the type of service.
   * If the type is "contracts", it initializes the service contracts.
   * If the type is "predictive", it initializes the predictive services.
   */
  private initWidget() {
    this.activeLoadSubscription?.unsubscribe();
    this.activeLoadSubscription = undefined;

    const type = this.type();
    const asset = this.asset();
    if (asset && asset.id) {
      const loadSubscription = new Subscription();
      this.activeLoadSubscription = loadSubscription;

      switch (type) {
        case "contracts":
          this.serviceContracts.set([]);
          this.initServiceContracts(loadSubscription);
          break;
        case "predictive":
          this.predictiveServices.set([]);
          this.initPredictiveServices(loadSubscription);
          break;
        default:
      }
    }
  }

  /**
   * Initializes the service contracts tab.
   * Loads all service contracts, filters them by the current asset if it is a gate system or a customer,
   * and creates the config for the service list component.
   */
  private initServiceContracts(loadSubscription: Subscription) {
    this.serviceListConfig = {
      title: "service_contracts.title",
      icon: "repair",
    };
    this.selectedTab.set("upcoming");
    this.loading.set(true);
    loadSubscription.add(
      this.serviceCotractService.getServiceContracts().subscribe({
        next: (incomingContracts) => {
          if (this.activeLoadSubscription !== loadSubscription) {
            return;
          }

          let relevantContracts = incomingContracts;
          const asset = this.asset();
          if (asset) {
            if (asset.assetType === ASSET_TYPES.GATE_SYSTEM) {
              relevantContracts = relevantContracts.filter((contract) => {
                return contract.gateSystemList && asset.id
                  ? contract.gateSystemList.map((gate) => gate.id).includes(asset.id)
                  : false;
              });
            } else if (asset.assetType === ASSET_TYPES.CUSTOMER) {
              relevantContracts = relevantContracts.filter((contract) => {
                return contract.customer?.id === asset.id;
              });
            }
          }
          this.serviceContracts.set(relevantContracts);
          this.loading.set(false);
        },
      })
    );
  }
  /**
   * Initializes the predictive services tab.
   * Fetches all gate systems under the given asset and loads the predictive services for each of them.
   * If the asset is a gate system, it directly loads the predictive services for the gate system.
   * @internal
   */
  private initPredictiveServices(loadSubscription: Subscription) {
    this.serviceListConfig = {
      title: "predictive_services.title",
      icon: "light_bulb",
    };
    this.selectedTab.set("remainingTime");

    const asset = this.asset();
    if (asset && asset.id) {
      this.loading.set(true);
      if (asset.assetType === ASSET_TYPES.GATE_SYSTEM) {
        this.getPredictiveServices([asset.id], loadSubscription);
      } else {
        loadSubscription.add(
          this.getAllGateSystemsOfAsset(asset.id, asset.assetType).subscribe({
            next: (gateSystemIds) => {
              if (this.activeLoadSubscription === loadSubscription) {
                this.getPredictiveServices(gateSystemIds, loadSubscription);
              }
            },
          })
        );
      }
    }
  }

  /**
   * Categorizes service contracts into past and future based on their service dates.
   *
   * @param gateContracts - An array of service contracts to evaluate.
   * @returns An object containing arrays of past and future contracts.
   */
  private extractPastAndFutureContracts(gateContracts: ServiceContract[]) {
    const pastContracts: ServiceEntry = [];
    const futureContracts: ServiceEntry = [];
    gateContracts.forEach((contract) => {
      if (contract.nextService && contract.contractNumber && contract.contractName) {
        // const nextService = DateTime.from(
        //   contract.nextService,
        //   "dd.MM.yyyy"
        // );
        const now = DateTime.now().toMillis();
        if (contract.nextService && contract.nextService > now) {
          futureContracts.push([
            contract.contractNumber,
            contract.contractName,
            DateTime.fromMillis(contract.nextService).toFormat("dd.MM.yyyy"),
          ]);
        }
        if (contract.dateOfService && contract.dateOfService > 0) {
          pastContracts.push([
            contract.contractNumber,
            contract.contractName,
            DateTime.fromMillis(contract.dateOfService).toFormat("dd.MM.yyyy"),
          ]);
        }
      }
    });
    return { pastContracts, futureContracts };
  }
  /**
   * Loads the predictive services for the given gate systems.
   * If the length of gateSystemIds is 0, it sets the loading flag to false.
   * @param gateSystemIds - Array of gate system IDs to load predictive services for.
   */
  private getPredictiveServices(gateSystemIds: string[], loadSubscription: Subscription) {
    const gateSystemsComponents$ = [];
    for (const gateSystemId of gateSystemIds) {
      gateSystemsComponents$.push(this.gateComponentsService.getGateComponents(gateSystemId));
    }
    if (gateSystemsComponents$.length === 0) {
      if (this.activeLoadSubscription === loadSubscription) {
        this.loading.set(false);
      }
      return;
    }
    loadSubscription.add(
      combineLatest(gateSystemsComponents$).subscribe({
        next: (gateSystemsComponentsRes) => {
          if (this.activeLoadSubscription !== loadSubscription) {
            return;
          }

          for (const gateComponents of gateSystemsComponentsRes) {
            if (gateComponents) {
              const predictiveServices = this.gateComponentsService.convertToPredictiveServices(gateComponents);
              this.predictiveServices.update((current) => {
                return [...current.concat(predictiveServices)];
              });
            }
          }
          this.loading.set(false);
          this.initRealtime(gateSystemIds);
        },
      })
    );
  }
  /**
   * Initializes the real-time WebSocket connection for the load changes.
   * @param distinctGateSystems gate systems for which to listen for load changes
   */
  private initRealtime(distinctGateSystemsIds: string[]) {
    this.timeseriesSubscription?.close();
    const subscription = this.timeseriesWebsocket.register({
      entityType: ASSET_TYPES.GATE_SYSTEM,
      entityId: distinctGateSystemsIds,
      keys: ["cycle"],
    });
    this.timeseriesSubscription = subscription;
    subscription.messages$.subscribe({
      next: (incomingData) => {
        this.handleRealtimeLoadChange(incomingData);
      },
      error: () => undefined,
    });
  }
  /**
   * Updates the load changes for predictive services that correspond to a given gate system.
   * @param realtimeTelemetry contains the gate system ID and the load change data
   */
  private handleRealtimeLoadChange(realtimeTelemetry: RealtimeTelemetryMessage) {
    const gateSystemId = realtimeTelemetry.entityId?.toString();
    this.predictiveServices.update((predictiveServices) => {
      const matchingPredictiveServices = predictiveServices.filter(
        (service) => service.gateSystem?.id?.toString() === gateSystemId
      );
      for (const predictiveService of matchingPredictiveServices) {
        const incomingLoadChange = realtimeTelemetry.data.cycle;
        if (predictiveService && incomingLoadChange !== undefined) {
          predictiveService.setRealtimeLoadChanges(incomingLoadChange.value);
        }
      }
      return [...predictiveServices];
    });
  }

  /**
   * Filters and maps the components to only include those with load changes or max load changes.
   * The mapped array contains the gate system, label and the remaining load changes.
   * @param components - The components to filter and map.
   * @returns An array of PredServiceLoadEntry.
   */
  private convertToComponentsByLoadChanges(components: PredictiveService[]) {
    const componentsByLoadChanges = components
      .filter((component: PredictiveService) => {
        return component.maintenanceDuringLoadChanges !== null || component.maxLoadChanges !== null;
      })
      .map((component: PredictiveService) => {
        let remainingLoadChanges = -1;
        if (
          isNotNullOrUndefined(component.currentReplacementLoadChanges) &&
          isNotNullOrUndefined(component.maxLoadChanges)
        ) {
          const remainingReplacement = component.maxLoadChanges - component.currentReplacementLoadChanges;

          remainingLoadChanges = remainingReplacement;
        }

        if (
          component.maintenanceDuringLoadChanges !== undefined &&
          component.maintenanceDuringLoadChanges !== null &&
          component.currentInspectionLoadChanges !== undefined &&
          component.currentInspectionLoadChanges !== null
        ) {
          const remainingInspection = component.maintenanceDuringLoadChanges - component.currentInspectionLoadChanges;
          if (remainingInspection < remainingLoadChanges) {
            remainingLoadChanges = remainingInspection;
          }
        }
        return [component.gateSystem, component.gateComponentLabel, remainingLoadChanges];
      })
      .sort((a, b) => {
        if (typeof a[2] === "number" && typeof b[2] === "number") {
          return a[2] - b[2];
        } else {
          return 0;
        }
      });
    return componentsByLoadChanges as PredServiceLoadEntry;
  }

  /**
   * Filters and maps the components to only include those with uptime maintenance or max operating time.
   * The mapped array contains the gate system, label and the time of the next inspection or replacement.
   * @param components - The components to filter and map.
   * @returns An array of PredServiceTimeEntry.
   */
  private convertToComponentsByTime(components: PredictiveService[]) {
    const componentsByTime = components
      .filter((component) => {
        return component.uptimeMaintenance !== null || component.maxOperatingTime !== null;
      })
      .map((component) => {
        let time = "";
        let remainingTime = -1;
        if (component.maxOperatingTime) {
          remainingTime = component.maxOperatingTime;
        }

        if (component.uptimeMaintenance) {
          if (component.uptimeMaintenance < remainingTime || remainingTime === -1) {
            remainingTime = component.uptimeMaintenance;
          }
        }

        if (remainingTime > -1 && component.startDate !== null && component.startDate !== undefined) {
          let startDate: number = component.startDate;

          if (component.dateOfInspection) {
            startDate = component.dateOfInspection;
          }
          time = DateTime.fromMillis(startDate).plus({ days: remainingTime }).toFormat("dd.MM.yyyy");
        }

        return [component.gateSystem, component.gateComponentLabel, time];
      })
      .sort((a, b) => {
        const timeA = a[2];
        const timeB = b[2];
        if (typeof timeA === "string" && typeof timeB === "string") {
          return (
            DateTime.fromFormat(timeA, "dd.MM.yyyy").toMillis() - DateTime.fromFormat(timeB, "dd.MM.yyyy").toMillis()
          );
        } else {
          return 0;
        }
      });
    return componentsByTime as PredServiceTimeEntry;
  }

  /**
   * Gets all gate systems associated with a given asset.
   * @param assetId the id of the asset
   * @param assetType the type of the asset
   * @returns an observable of an array of gate system ids
   */
  private getAllGateSystemsOfAsset(assetId: string, assetType: string) {
    return this.assetHierarchyService.getAssetHierarchy(assetType, 4).pipe(
      map((hierarchy: AssetHierarchyDTO[] | null) => {
        let matchingCustomer!: AssetHierarchyDTO;
        if (this.permissions.hasPermission(this.permissions.allPermissions.ASSET_MASTER_DATA_CREATE)) {
          // Highest element in hierarchy is tenant:
          if (hierarchy) {
            const potentialMatch = hierarchy[0].children?.find((asset) => asset.id === assetId);
            if (potentialMatch) {
              matchingCustomer = potentialMatch;
            }
          }
        } else {
          const potentialMatch = hierarchy?.find((asset) => asset.id === assetId);
          if (potentialMatch) {
            matchingCustomer = potentialMatch;
          }
        }
        if (matchingCustomer) {
          const gateSystems = this.extractGateSystems(matchingCustomer);
          return gateSystems.map((gateSystem) => gateSystem.id);
        } else {
          return [];
        }
      })
    );
  }

  /**
   * Recursively traverses the asset hierarchy starting from the given root asset and extracts all gate systems.
   * @param rootAsset the root asset of the hierarchy to be traversed
   * @returns an array of gate systems (AssetHierarchyDTO)
   */
  private extractGateSystems(rootAsset: AssetHierarchyDTO) {
    const gateSystems: AssetHierarchyDTO[] = [];
    if (rootAsset.children) {
      for (const childAsset of rootAsset.children) {
        if (childAsset.assetType === ASSET_TYPES.GATE_SYSTEM) {
          gateSystems.push(childAsset);
        } else {
          gateSystems.push(...this.extractGateSystems(childAsset));
        }
      }
    }
    return gateSystems;
  }
  /**
   * Selects the given tab in the service list component.
   * @param tab The key of the tab to select.
   */
  public selectTab(tab: string) {
    this.selectedTab.set(tab);
  }

  /**
   * Casts the provided value to a number.
   * @param value The value to cast, which can be a number, string, or GateSystemRef.
   * @returns The value cast to a number.
   */
  public getNumber(value: number | string | GateSystemRef) {
    return value as number;
  }
  /**
   * Checks if the given value is of type number.
   * @param value The value to check, which can be a number, string, or GateSystemRef.
   * @returns True if the value is a number, false otherwise.
   */

  public isNumber(value: number | string | GateSystemRef) {
    return typeof value === "number";
  }
  /**
   * If the given value is a GateSystemRef, it is returned as is. Otherwise, it is
   * cast to a GateSystemRef.
   * @param gateSystem The value to cast, which can be a number, string, or GateSystemRef.
   * @returns The given value as a GateSystemRef, which might be a cast.
   */
  public getGateSystemRef(gateSystem: number | string | GateSystemRef) {
    return gateSystem as GateSystemRef;
  }
}
